import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "../lib/cn";

export type IconButtonVariant = "ghost" | "secondary" | "danger";
/** `lg` matches the 40px page-header controls. */
export type IconButtonSize = "sm" | "md" | "lg";

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
}

const variantClasses: Record<IconButtonVariant, string> = {
  ghost: "text-secondary hover:bg-surface-elevated hover:text-primary",
  secondary: "border border-border bg-surface text-secondary hover:bg-surface-elevated hover:text-primary",
  danger:
    "border border-status-danger/20 text-status-danger/70 hover:bg-status-danger/5 hover:text-status-danger",
};

const sizeClasses: Record<IconButtonSize, string> = {
  sm: "density-square-sm rounded-control",
  md: "density-square-md rounded-control",
  lg: "density-square-lg rounded-control",
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = "ghost", size = "sm", className, type = "button", children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex shrink-0 items-center justify-center transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-40",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});